import { useState, useEffect } from "react";
import { Cloud, Globe, Settings, TrendingUp } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const safeJson = async (res) => {
  try { return await res.json(); } catch (e) {
    if (e.message?.includes("body stream already read") || e.message?.includes("locked")) {
      try { return await res.clone().json(); } catch { return {}; }
    }
    return {};
  }
};


// Configuration stockée
const CONFIG = {
  googleCloud: {
    projectName: "My Maps Project",
    clientId: "895754927785-mlcobf418f3gj55g27l8u901dmmg32rb.apps.googleusercontent.com",
    clientType: "Application Web",
    redirectUris: [
      `${getApiUrl()}/api/google-ads/auth/callback`
    ]
  },
  googleAds: {
    developerToken: "T9nc6vlRET8LFWMtnPu3xg",
    tokenStatus: "Acces de base (Production)",
    managerId: "592-247-8345",
    managerName: "SANI ADAPT Manager",
    customerId: "279-487-2550",
    customerName: "ENERGO SYM"
  }
};

function ConfigRow({ label, value, mono = false }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-800">
      <span className="text-slate-400">{label}</span>
      <span className={`text-white ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}

function ConfigPanel() {
  return (
    <div className="bg-slate-900/50 rounded-xl border border-slate-700/50 overflow-hidden mt-4">
      <div className="p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
            <Cloud className="w-4 h-4 text-blue-400" />
          </div>
          <h5 className="font-semibold text-blue-400">Google Cloud Console</h5>
        </div>
        <div className="space-y-2 text-sm">
          <ConfigRow label="Projet" value={CONFIG.googleCloud.projectName} />
          <ConfigRow label="Type OAuth" value={CONFIG.googleCloud.clientType} />
          <ConfigRow label="Client ID" value={CONFIG.googleCloud.clientId.substring(0, 25) + "..."} mono />
          <div className="py-1.5">
            <span className="text-slate-400 block mb-2">URI de redirection</span>
            <div className="space-y-1 text-xs">
              <div><span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 mr-2">Preview</span><code className="text-slate-300">{CONFIG.googleCloud.redirectUris[0]}</code></div>
              <div><span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 mr-2">Prod</span><code className="text-slate-300">{CONFIG.googleCloud.redirectUris[1]}</code></div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          <h5 className="font-semibold text-green-400">Google Ads API</h5>
        </div>
        <div className="space-y-2 text-sm">
          <ConfigRow label="Developer Token" value={CONFIG.googleAds.developerToken} mono />
          <ConfigRow label="Statut" value={CONFIG.googleAds.tokenStatus} />
          <ConfigRow label="Manager (MCC)" value={`${CONFIG.googleAds.managerId} - ${CONFIG.googleAds.managerName}`} />
          <ConfigRow label="Compte Pub" value={`${CONFIG.googleAds.customerId} - ${CONFIG.googleAds.customerName}`} />
        </div>
      </div>
      <div className="px-4 py-3 bg-slate-800/50 border-t border-slate-700/50 flex gap-4">
        <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
          <Globe className="w-3 h-3" /> Cloud Console
        </a>
        <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1">
          <Globe className="w-3 h-3" /> Google Ads
        </a>
      </div>
    </div>
  );
}

export default function GoogleAdsIntegrationCard() {
  const [status, setStatus] = useState({ loading: true, connected: false });
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    checkStatus();
    const handleMsg = (e) => {
      if (e.data?.type === 'google-ads-auth') {
        setConnecting(false);
        e.data.success ? checkStatus() : setError(e.data.message);
      }
    };
    window.addEventListener('message', handleMsg);
    return () => window.removeEventListener('message', handleMsg);
  }, []);

  const checkStatus = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/auth/status`);
      setStatus({ loading: false, ...(await safeJson(res)) });
    } catch { setStatus({ loading: false, connected: false }); }
  };

  const connect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const data = await safeJson(await authFetch(`${API}/google-ads/auth/url`));
      if (data.auth_url) {
        const popup = window.open(auth_url, 'google-ads-auth', 'width=600,height=700');
        if (!popup) { setError("Popup bloqué"); setConnecting(false); return; }
        const chk = setInterval(() => { if (popup?.closed) { clearInterval(chk); setConnecting(false); setTimeout(checkStatus, 1000); }}, 1000);
      }
    } catch { setError("Erreur connexion"); setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter ?")) return;
    await authFetch(`${API}/google-ads/auth/disconnect`, { method: "POST" });
    checkStatus();
  };

  if (status.loading) return <div className="text-center py-4 text-slate-400">Chargement...</div>;

  const isConnected = status.connected;
  
  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-5 ${isConnected ? 'bg-green-500/5 border-green-500/30' : 'bg-slate-800/30 border-slate-700/50'}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isConnected ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
              <TrendingUp className={`w-6 h-6 ${isConnected ? 'text-green-400' : 'text-blue-400'}`} />
            </div>
            <div>
              <h4 className="font-semibold">Google Ads</h4>
              <p className="text-sm text-slate-400">{isConnected ? `ID: ${status.customer_id}` : 'Campagnes publicitaires'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowConfig(!showConfig)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs flex items-center gap-1">
              <Settings className="w-3 h-3" /> {showConfig ? 'Masquer' : 'Config'}
            </button>
            <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isConnected ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
              {isConnected ? '✓ Connecté' : 'Non connecté'}
            </span>
            {isConnected ? (
              <button onClick={disconnect} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm">Déconnecter</button>
            ) : (
              <button onClick={connect} disabled={connecting} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm">
                {connecting ? 'Connexion...' : 'Connecter'}
              </button>
            )}
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
      </div>
      {showConfig && <ConfigPanel />}
    </div>
  );
}
